import FirebaseAdapter from "@wc/adapters/firebase";
import {
  createGoalLockedEvent,
  createGoalUnlockedEvent,
} from "@wc/domain/teams/core/events-service";

/**
 * @typedef {object} Goal
 * @property {string} [id] The goal id (undefined for new goals)
 * @property {string} text The goal text
 * @property {boolean} completed Whether the goal is completed
 * @property {string} organizationId The organization this goal belongs to
 * @property {string} teamId The team this goal is associated with
 */

/**
 * @typedef {object} GoalInput
 * @property {string} [id] The goal id (undefined for new goals)
 * @property {string} text The goal text
 * @property {boolean} completed Whether the goal is completed
 * @property {string} organizationId The organization this goal belongs to
 * @property {string} teamId The team this goal is associated with
 */

/**
 * Get all goals for a user's week in a specific organization
 * @param {string} userId The user id
 * @param {string} weekOf The Monday date key (yyyy-MM-dd)
 * @param {string} [organizationId] The organization to filter by
 * @returns {Promise<Goal[]>} List of goals for the week
 */
export async function getWeekGoals(userId, weekOf, organizationId) {
  const goals = await FirebaseAdapter().goals.listGoalsForWeek(
    userId,
    weekOf,
    organizationId,
  );

  return goals.map((g) => ({
    id: g.id,
    text: g.text,
    completed: g.completed,
    organizationId: g.organizationId,
    teamId: g.teamId,
  }));
}

/**
 * Save a goal (create or update)
 * @param {GoalInput} goalData The goal data
 * @param {string} userId The user id
 * @param {string} weekOf The Monday date key (yyyy-MM-dd)
 * @returns {Promise<string>} The goal id
 */
export async function saveGoal(goalData, userId, weekOf) {
  const goalToSave = {
    id: goalData.id,
    userId,
    organizationId: goalData.organizationId,
    teamId: goalData.teamId,
    weekOf,
    text: goalData.text,
    completed: goalData.completed,
  };

  if (goalData.completed && !goalData.id) {
    goalToSave.completedAt = new Date();
  }

  return FirebaseAdapter().goals.saveGoal(goalToSave);
}

/**
 * Delete a goal (soft delete)
 * @param {string} goalId The goal id
 * @param {string} userId The user performing the deletion
 * @returns {Promise<void>}
 */
export async function deleteGoal(goalId, userId) {
  return FirebaseAdapter().goals.deleteGoal(goalId, userId);
}

/**
 * Check if a week's goals are locked for a specific organization
 * @param {string} userId The user id
 * @param {string} organizationId The organization id
 * @param {string} weekOf The Monday date key (yyyy-MM-dd)
 * @returns {Promise<boolean>} Whether the week is locked
 */
export async function isWeekLocked(userId, organizationId, weekOf) {
  const weekData = await FirebaseAdapter().goals.getGoalWeek(
    userId,
    organizationId,
    weekOf,
  );
  return weekData?.lockedAt != null;
}

/**
 * Lock a week's goals for a specific organization
 * @param {string} userId The user id
 * @param {string} organizationId The organization id
 * @param {string} weekOf The Monday date key (yyyy-MM-dd)
 * @param {string} [teamId] The team id (optional, for creating events)
 * @returns {Promise<void>}
 */
export async function lockWeek(userId, organizationId, weekOf, teamId) {
  await FirebaseAdapter().goals.lockWeek(userId, organizationId, weekOf);

  if (teamId) {
    try {
      await createGoalLockedEvent(teamId, userId, weekOf);
    } catch (error) {
      console.error("Failed to create lock event:", error);
    }
  }
}

/**
 * Unlock a week's goals for a specific organization
 * @param {string} userId The user id
 * @param {string} organizationId The organization id
 * @param {string} weekOf The Monday date key (yyyy-MM-dd)
 * @param {string} [teamId] The team id (optional, for creating events)
 * @returns {Promise<void>}
 */
export async function unlockWeek(userId, organizationId, weekOf, teamId) {
  await FirebaseAdapter().goals.unlockWeek(userId, organizationId, weekOf);

  if (teamId) {
    try {
      await createGoalUnlockedEvent(teamId, userId, weekOf);
    } catch (error) {
      console.error("Failed to create unlock event:", error);
    }
  }
}

/**
 * Check if the week review modal should be shown and get previous week's goals
 * @param {string} userId The user id
 * @param {string} organizationId The organization id
 * @param {string} currentWeekOf Current week key (yyyy-MM-dd)
 * @param {string} previousWeekOf Previous week key (yyyy-MM-dd)
 * @returns {Promise<{shouldShow: boolean, previousGoals: Goal[]}>} Review status and previous goals
 */
export async function checkWeekReviewStatus(
  userId,
  organizationId,
  currentWeekOf,
  previousWeekOf,
) {
  const [currentWeekData, previousGoals] = await Promise.all([
    FirebaseAdapter().goals.getGoalWeek(userId, organizationId, currentWeekOf),
    getWeekGoals(userId, previousWeekOf, organizationId),
  ]);

  const alreadyReviewed = currentWeekData?.previousWeekReviewed === true;
  const hasPreviousGoals = previousGoals.length > 0;

  return {
    shouldShow: !alreadyReviewed && hasPreviousGoals,
    previousGoals,
  };
}

/**
 * Mark that the user has reviewed the previous week's goals
 * @param {string} userId The user id
 * @param {string} organizationId The organization id
 * @param {string} weekOf The Monday date key of the CURRENT week (yyyy-MM-dd)
 * @returns {Promise<void>}
 */
export async function markPreviousWeekReviewed(userId, organizationId, weekOf) {
  await FirebaseAdapter().goals.markPreviousWeekReviewed(
    userId,
    organizationId,
    weekOf,
  );
}
